import { defineComponent as _defineComponent } from 'vue'
import IconNotificationBell from '@/components/icons/IconNotificationBell.vue';
import ButtonPrimary from '@/components/input-controls/ButtonPrimary.vue';
import ModalResponsive from '@/components/layouts/ModalResponsive.vue';
import {CustomerProfileStore, PushService, SessionStore, UIStore} from '@customer-portal/shared';
import type {QDialog} from 'quasar';
import {computed, onMounted, ref, watch} from 'vue';

// QDialog pass-through boilerplate (matches PickupTimeChangedDialog)

export default /*#__PURE__*/_defineComponent({
  __name: 'PushNotificationPrompt',
  setup(__props, { expose }) {

// component: PushNotificationPrompt
//
// Soft-ask shown before the OS push-permission dialog, so a customer who isn't interested can say no
// without us spending their one-shot native prompt on a denial (once iOS records a denial the state
// moves to `denied` permanently and can only be undone in Settings).
//
// Driven by UIStore.showPushNotificationRequest rather than Dialog.create(), because the code that
// decides to open it -- PushService -- lives in @customer-portal/shared and cannot import a component
// out of the generic app package.
//
// No z-index override here on purpose. Some dialogs in this repo carry an unscoped
// `.q-dialog.<their-class> { z-index: 6010 }` block, but that never matches: QDialog portals its node
// to <body> and drops the wrapper's class on the way. It doesn't need to match either -- the portal
// node is created on show and appended last, so at Quasar's flat z-index of 6000 the most recently
// shown dialog is already on top, and this prompt is shown last (end of handleScreenPops).

const tplDialog = ref<QDialog | null>(null); // template ref
const hide = () => {
	tplDialog.value?.hide();
};
const show = () => {
	tplDialog.value?.show();
};
expose({show, hide});

// What we will hand back to whoever awaited PushService.enablePushNotifications(). Defaults to false
// so any close path we haven't explicitly handled still settles as "not enabled" rather than lying.
const promptResult = ref(false);

// ModalResponsive owns its own visibility and exposes show()/hide() instead of a v-model, so mirror
// the store flag onto it rather than binding.
watch(() => UIStore.showPushNotificationRequest, (showRequest: boolean) => {
	if(showRequest) {
		promptResult.value = false;
		show();
	} else {
		hide();
	}
});

// The watcher above only fires on a change, so a mount that happens while the flag is already true
// would never show the dialog -- and whoever awaited enablePushNotifications() would wait forever.
onMounted(() => {
	if(UIStore.showPushNotificationRequest) {
		show();
	}
});

const firstName = computed((): string => {
	return CustomerProfileStore.customerProfile?.firstName ?? '';
});

const title = computed((): string => {
	// Personalised when we know the name, but it degrades to the original wording for profiles that
	// never filled one in -- guests and social logins often have no first name.
	return firstName.value
		? `One last thing ${firstName.value}, Allow push notifications`
		: 'One last thing, Allow push notifications';
});

const body = computed((): string => {
	const brandName = SessionStore.brandConfig?.brandName || '';
	return `Allow ${brandName} to notify you about personalized Offers, Rewards, and other Specials.`;
});

// We stay open behind the OS dialog, so the Allow button is still tappable while a request is in
// flight. Track it rather than let a second tap fire a second requestPermissions().
const requesting = ref(false);

const allow = async () => {
	if(requesting.value) {
		return;
	}
	requesting.value = true;
	try {
		// Fires the real OS dialog. We stay open behind it so the customer keeps the context for what
		// they're being asked, and so a denial lands back on our own "push is disabled" recovery modal.
		promptResult.value = await PushService.requestPermission();
	} catch(error) {
		// Nothing to recover here, but we must still fall through to hide(): this dialog is persistent
		// with no close button, so bailing out early would trap the customer behind it and leave the
		// promise the profile toggle is awaiting pending forever.
		console.error('Failed to request push notification permission', error);
		promptResult.value = false;
	} finally {
		requesting.value = false;
		hide();
	}
};

const decline = () => {
	// Mirrors the guard in allow(): this link stays tappable while requestPermission() is in flight --
	// during register(), and before the OS dialog animates in -- and a decline landing there would
	// settle the promise false even though permission was actually granted. hide() on an already-hidden
	// dialog emits nothing, so allow()'s `true` would never reach onHide.
	if(requesting.value) {
		return;
	}
	// Deliberately does NOT call requestPermission() -- declining the soft-ask leaves the OS permission
	// state untouched at `prompt`, so the customer can still turn push on from their profile without
	// a trip through system settings. It does end the automatic asking: showing this modal already
	// recorded PUSH_NOTIFICATIONS as requested, which persists until logout.
	promptResult.value = false;
	hide();
};

const onHide = () => {
	UIStore.setShowPushNotificationRequest(false);
	PushService.resolvePushNotificationPrompt(promptResult.value);
};

return { __sfc: true,tplDialog, hide, show, promptResult, firstName, title, body, requesting, allow, decline, onHide, IconNotificationBell, ButtonPrimary, ModalResponsive }
}

})